import type { ComponentDocs } from '../component-docs.js';
import type { CaosPicklistOption } from './picklist-field.js';

/** What the surrounding page hands the Event field, keyed by the Event kind field's own value. */
const EVENTS_BY_KIND: Record<string, CaosPicklistOption[]> = {
  created: [
    { value: 'record_created', label: 'A record is created' },
    { value: 'field_set_on_create', label: 'A field is set on create' },
  ],
  updated: [
    { value: 'field_changed', label: 'A field changes value' },
    { value: 'status_changed', label: 'A status changes' },
  ],
};

/**
 * What `<caos-picklist-field>` says about itself.
 *
 * ONE VALUE OUT OF A SET THAT IS ALREADY KNOWN. The options are given to it; it never fetches them
 * on its own initiative. That is still the whole line between this piece and LookupField, which
 * searches a live record set — this piece's set may now be FED to it by a dependency (below), but
 * it is still handed the whole answer at once rather than queried per keystroke against a server.
 *
 * TWO WAYS TO GIVE IT THE OPTIONS, and they are exclusive. Either set the `options` property — an
 * array of `{ value, label, qualifier? }`, or of plain strings, which are expanded into both — or
 * declare `<option>` children in the light DOM, which are read ONCE at connect and only when the
 * property has not been set. Both are shown below. The declared form is the one that survives being
 * written as markup; the property is the one a page with data behind it uses.
 *
 * THE OPTIONS ARE NOT SLOTTED. There is no `<slot>` anywhere in the shadow root: declared
 * `<option>` children are READ as data and then rebuilt inside the inner `<select>`. That is why
 * `slots` below is empty even though the examples put children inside the element — placing
 * anything else in there draws nothing.
 *
 * THE PLACEHOLDER IS A DISABLED FIRST CHOICE. Setting `placeholder` prepends a hidden, disabled,
 * empty-valued option and leaves it selected until a real one is picked, which is how "nothing
 * chosen yet" is told apart from "the first option is chosen". Without a placeholder a select has
 * no empty state and the first option is the answer whether anybody meant it or not.
 *
 * CAOS-569 — A DEPENDENT DROPDOWN, DECLARED, NOT CODED. `dependsOn` names the field this one waits
 * on; `dependsOnValue` is that field's current value, kept in sync by the host exactly the way
 * `value` already is. While `dependsOn` is set and `dependsOnValue` is empty, the field renders
 * EMPTY of real choices — its placeholder still showing, still enabled — because there is nothing
 * yet to choose FROM. The moment the host writes a non-empty `dependsOnValue`, an `optionsSource`
 * set alongside it is called with that value and its answer becomes the option list; without an
 * `optionsSource` the host may still set `.options` itself, and the waiting state above still
 * applies for free. One field, wired two different ways by two different placements — the "list B
 * depends on list A" behaviour is never coded per screen.
 *
 * A QUALIFIER LINE AND A FILTERABLE RENDER, TOGETHER. An option's `qualifier` — what the option IS,
 * not merely its name — only ever shows once `filterable` is set: a native `<option>` has nowhere to
 * put a second line, so `filterable` swaps the native select for a typeahead over a listbox that can
 * draw one. The same swap is worth reaching for on a long plain list even with no qualifiers, for
 * the reason its own `insteadUse` entry below states: once a set is long enough to want typing over
 * scanning. Typing filters by substring against BOTH lines, so an option is found by what it is
 * called OR by what it is.
 *
 * CAOS-597 — SEVERAL VALUES FROM THE SAME LIST. `multiple` draws the same options as a group of
 * checkboxes named by the label, and what the field holds becomes `values`, a list. It is a
 * presentation of this field and not a field of its own, because what is chosen from is still one
 * declared list — the value set a MultiSelect field draws from. When the ORDER of what is chosen
 * matters, that is the ordered picker instead.
 */
const docs: ComponentDocs = {
  aliases: ['select', 'dropdown', 'combo box', 'choice field', 'enum field', 'option list', 'typeahead', 'dependent picklist', 'cascading dropdown', 'multi-select', 'checkbox group', 'multiple choice'],

  answers: [
    'let somebody pick one of a few fixed options',
    'edit a status, a type or a category on a record',
    'stop somebody typing a value that is not on the list',
    'show that nothing has been chosen yet',
    'make one dropdown wait on another one before it offers anything',
    'let somebody search a long list of options by what they are called or what they are',
    'let somebody choose several values from the same list',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'options',
      // No attribute: a list of value/label pairs cannot honestly be put in a string. Declare
      // `<option>` children instead when the set has to be expressible as markup.
      type: 'json',
      description:
        'The selectable values, as an array of `{ value, label, qualifier?, image?, icon?, description?, amount? }` ' +
        'or of plain strings. Setting it replaces the whole list and takes precedence over any declared `<option>` ' +
        'children. `qualifier` — a quieter second line under the label, what the option IS rather than merely its ' +
        'name — only ever shows once `filterable` is set; a native option has nowhere to draw a second line. ' +
        '`image`, `icon`, `description` and `amount` are what an option is drawn FROM in the cards presentation ' +
        '(see `presentation`), each optional and each simply not drawn when absent; outside that presentation they ' +
        'are carried and unshown, except that a `description` stands in for a missing `qualifier`.',
    },
    {
      name: 'filterable',
      attribute: 'filterable',
      property: true,
      type: 'boolean',
      description:
        'Draw a typeahead over a listbox instead of the native select. Required for a `qualifier` to actually ' +
        'show, and worth reaching for on its own once the set is long enough that typing beats scanning. Typing ' +
        "filters by substring against BOTH an option's label and its qualifier, case-insensitively. The native " +
        'select stays underneath, hidden, so `name` still forwards a value into a submitted form exactly as before.',
      default: false,
    },
    {
      name: 'presentation',
      attribute: 'presentation',
      type: 'text',
      // A CLOSED set, declared as such: the kernel refuses anything else at deploy, so the page
      // editor must offer these three rather than a free text box somebody can misspell into a
      // rejected deploy. Same shape as `mode`, `variant`, `size` and every other closed input here.
      options: ['dropdown', 'cards', 'radio'],
      description:
        'How the same declared list is drawn: `dropdown` (the default); `cards`, a row of selectable cards ' +
        "showing whichever of an option's `image`, `icon`, `description` and `amount` it carries; or `radio`, " +
        'plain radio buttons with every option visible. Each is a presentation of one list rather than a ' +
        'different field, so `value`, `name`, `required`, `disabled`, `invalid` and `message` all mean what they ' +
        'always did and the native select still submits. `multiple` wins over all of them — several values is ' +
        'the checkbox group — and `cards` and `radio` each displace `filterable`. Anything unrecognised reads as ' +
        '`dropdown`, so a presentation nothing can draw degrades to the list rather than to an empty field. ' +
        'Which one a real field uses is declared by the value set its options come from. The radio buttons are ' +
        "the platform's own, so arrow-key movement, the single tab stop and the checked semantics are too.",
      default: 'dropdown',
    },
    {
      name: 'multiple',
      attribute: 'multiple',
      property: true,
      type: 'boolean',
      description:
        'Choose several values instead of one: the same options drawn as a group of checkboxes named by the label, ' +
        'and what the field holds becomes `values`. `required`, `disabled`, `invalid`, `message` and `name` ' +
        'apply to the group; `filterable`, `placeholder` and `value` do nothing. Once somebody has used a required ' +
        'group and left nothing chosen, it shows as invalid on its own.',
      default: false,
    },
    {
      name: 'values',
      // No attribute: a list of values cannot be written in one.
      type: 'json',
      description:
        'What is chosen when `multiple` is set, as a list of option values, written back on every change in the ' +
        'order the options list them. A value not among the options is kept, after the rest, and not drawn — so a ' +
        'value since retired from the list is not erased by the next save.',
    },
    {
      name: 'dependsOn',
      attribute: 'depends-on',
      property: true,
      type: 'fieldRef',
      description:
        "The field this dropdown waits on — declarative only, naming the relationship as metadata rather than " +
        "coding it into a screen. Pair it with `dependsOnValue`, which the host keeps in sync with that field's " +
        'actual value; this input by itself does not fetch or filter anything.',
    },
    {
      name: 'dependsOnValue',
      attribute: 'depends-on-value',
      property: true,
      type: 'text',
      description:
        "The CURRENT value of the field named by `dependsOn`, written by the host every time it changes — the " +
        'same "data in" shape `value` already is. While `dependsOn` is set and this is empty, the field renders ' +
        'with NO real options — its placeholder still showing, still enabled and focusable — because there is ' +
        'nothing yet to choose from; that is the whole of the waiting state, and it needs no other input to say ' +
        'so. Becoming non-empty calls `optionsSource`, if one is set, with the new value.',
    },
    {
      name: 'optionsSource',
      // No attribute: a function cannot honestly be put in a string, the same reason `search` on
      // CaosLookupField has none.
      type: 'json',
      description:
        'The injected result source for a dependent dropdown — `(dependsOnValue) => options | Promise<options>`, ' +
        'in the same "data in, not queried" shape as `CaosLookupField.search`. Called once when `dependsOnValue` ' +
        'first becomes non-empty, and again every time it changes to a different value; whatever it returns ' +
        'becomes the option list. Optional — a host may instead set `.options` itself in response to the same ' +
        'change, and the waiting state above still applies without this.',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        "The field's name, drawn above the control and pointed at the select, so clicking it focuses the " +
        'control. It is also used as the accessible name.',
    },
    {
      name: 'placeholder',
      attribute: 'placeholder',
      type: 'text',
      description:
        'The "nothing chosen yet" line. Prepends a hidden, disabled, empty-valued first option and leaves it ' +
        'selected, drawn muted, until a real value is picked. Without it the first option is selected from the start.',
    },
    {
      name: 'value',
      attribute: 'value',
      property: true,
      type: 'text',
      description:
        'The chosen option\'s `value`, not its label. It is written back on every change, so reading the ' +
        'attribute is reading the current selection.',
    },
    {
      name: 'name',
      attribute: 'name',
      type: 'text',
      description: 'The form name forwarded to the inner select, for a field submitted as part of a form.',
    },
    {
      name: 'required',
      attribute: 'required',
      type: 'boolean',
      description: 'A value has to be chosen. Draws the asterisk beside the label and marks the select required.',
      default: false,
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      type: 'boolean',
      description: 'You may not change the selection. Greys the control and refuses focus.',
      default: false,
    },
    {
      name: 'invalid',
      attribute: 'invalid',
      type: 'boolean',
      description: 'The selection is wrong — usually nothing chosen on a required field. Turns the outline and the message red.',
      default: false,
    },
    {
      name: 'message',
      attribute: 'message',
      type: 'text',
      description: 'The line under the control — muted helper text on its own, the red reason when `invalid` is also set.',
    },
    {
      name: 'inline',
      attribute: 'inline',
      type: 'boolean',
      description:
        'Draw the record-detail variant instead of the boxed form control: no box, an accent underline, ' +
        'inheriting the surrounding type, with the chevron pulled in tight. The label and the message are hidden.',
      default: false,
    },
  ],

  // A plain DOM `change`, re-dispatched from the shadow root with `composed` so it crosses the
  // boundary. There is no `input` event: a select has no intermediate state to report.
  events: [
    {
      name: 'change',
      when:
        'When a different option is chosen — or, with `multiple`, when any box is ticked or cleared — after `value` ' +
        'or `values` has been written back.',
      carries: 'nothing',
    },
  ],

  // No <slot> in the shadow root. `<option>` children are read as data at connect — see the header.
  // Nothing to read back that cannot also be set. `value` can be read off the element, but a page can
  // set it too, so it is documented as an input rather than here.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of the label, the control and the message.' },
    { token: '--caos-text-body', controls: 'The size of the label and of the chosen value.' },
    { token: '--caos-space-1', controls: 'The gap between the label, the control and the message.' },
    { token: '--caos-space-3', controls: 'The vertical padding inside the control, and the chevron inset.' },
    { token: '--caos-space-5', controls: 'With --caos-space-2, the room kept on the right so the value never runs under the chevron.' },
    { token: '--caos-space-2', controls: 'Part of that same right-hand inset.' },
    { token: '--caos-radius', controls: 'The corner radius of the control.' },
    { token: '--caos-color-border', controls: 'The outline at rest.' },
    { token: '--caos-color-border-strong', controls: 'The outline while the control has focus.' },
    { token: '--caos-color-surface-2', controls: 'The fill of the control.' },
    { token: '--caos-color-text', controls: 'The colour of the chosen value.' },
    { token: '--caos-color-text-muted', controls: 'The label, the unchosen placeholder, the chevron, the message and disabled text.' },
    { token: '--caos-color-accent', controls: 'The hover outline, and the underline of the inline variant.' },
    { token: '--caos-accent-wash', controls: 'The tint that fills the control while it has focus, in place of a focus ring.' },
    { token: '--caos-color-danger', controls: 'The required asterisk, and the outline and message when invalid.' },
    { token: '--caos-state-selected-bg', controls: 'The fill of the chosen card, and of the chosen row in the filterable list.' },
  ],

  parts: [
    { part: 'label', is: 'the label above the control' },
    { part: 'select', is: 'the select element' },
    { part: 'message', is: 'the line under the control' },
    { part: 'combo', is: 'the typeahead box, once `filterable` is set' },
    { part: 'input', is: 'the text input inside the typeahead' },
    { part: 'listbox', is: 'the filtered results list, once `filterable` is set' },
    { part: 'option', is: 'one result in that list' },
    { part: 'group', is: 'the group of checkboxes, once `multiple` is set' },
    { part: 'radios', is: 'the group of radio buttons, in the radio presentation only' },
    { part: 'radio', is: 'one option in the radio presentation — the label wrapping its button and its text' },
    { part: 'cards', is: 'the row of cards, once `presentation` is `cards`' },
    { part: 'card', is: 'one card in that row' },
  ],

  methods: [
    {
      name: 'focus',
      description: 'Move keyboard focus to the select, so typing a letter jumps to an option. The record-detail editor calls it on click-to-edit.',
      args: [
        {
          name: 'options',
          type: 'FocusOptions',
          description: 'The ordinary focus options — pass `{ preventScroll: true }` to focus without scrolling to it.',
        },
      ],
    },
  ],

  placing:
    'Placed as an item in a page section like any other field. The list itself is drawn by the browser in its ' +
    'own top layer, so nothing that holds this piece can clip it and no room has to be reserved for it. What ' +
    'the page owes it is the option list, and a placeholder when "nothing chosen" is a real state.',

  whenToUse:
    'One value out of a set that is already known — handed over whole, whether that is a short fixed list read ' +
    'without typing or a longer one (optionally depending on another field) searched with `filterable`. The ' +
    'line that never moves is "known set, handed over" versus "queried per keystroke against live records" — ' +
    'the moment it is the latter, reach for one of the pieces below instead.',

  insteadUse: [
    {
      name: 'LookupField',
      when: 'The value points at another RECORD, so the options have to be searched live rather than handed over as a known set — even a `filterable` picklist is still given its whole answer at once.',
    },
    {
      name: 'FieldRenderer',
      when: 'The type is not known until the record is read — hand it the field metadata and it draws the right control, and edits a multi-select as a group of checkboxes.',
    },
    {
      name: 'OrderedPicker',
      when: 'Several values are chosen and their ORDER is part of the answer — columns left to right, entries top to bottom.',
    },
    { name: 'CheckboxField', when: 'There are exactly two answers and they are yes and no.' },
    { name: 'SegmentedFilter', when: 'The choice changes what is shown rather than recording a value on a record.' },
    { name: 'OverflowMenu', when: 'The list is a set of actions to perform rather than a value to store.' },
  ],

  accessibility:
    'By default the control is a real `<select>` with a generated id and a label pointing at it, so it is ' +
    'announced with its name, clicking the label focuses it, and the whole native picker comes with it: ' +
    'keyboard opening, arrow keys, type-ahead by first letter, and the platform list on a touch device. Setting ' +
    '`filterable` swaps it for a real `role="combobox"` (`aria-expanded`, `aria-controls`, ' +
    '`aria-autocomplete="list"`) over a `role="listbox"` of options, with an `aria-activedescendant` the arrow ' +
    'keys move rather than DOM focus — the same pattern LookupField uses for the same reason. `required` and ' +
    '`invalid` are forwarded as `required`/`aria-invalid` in both renders, and the label is also set as ' +
    '`aria-label`. A dependency that is not yet satisfied never disables the control — it stays focusable and ' +
    'its placeholder stays readable, which is what tells "waiting" apart from "refused". What is left to the ' +
    'author is the option LABELS and qualifiers — they are all a person has to tell the options apart — and the ' +
    'placeholder wording, which is what says nothing has been chosen. With `multiple`, the boxes sit in a ' +
    '`role="group"` named by the label and described by the message, each a real checkbox; a required group ' +
    'that has been used and left empty is marked `aria-invalid` and its message says so.',

  examples: [
    {
      key: 'options-data',
      label: 'Options as data',
      summary: 'The list handed over as value/label pairs, with a placeholder holding the "nothing chosen yet" state.',
      placement: {
        component: 'caos-picklist-field',
        inputs: {
          label: 'Order type',
          placeholder: 'Choose a type',
          options: [
            { value: 'standard', label: 'Standard' },
            { value: 'renewal', label: 'Renewal' },
            { value: 'replacement', label: 'Replacement' },
          ],
        },
      },
    },
    {
      key: 'options-markup',
      label: 'Options declared in markup',
      summary:
        'The same field with its options written as `<option>` children instead — read once at connect, and the ' +
        'form that survives being written as plain markup.',
      placement: {
        component: 'caos-picklist-field',
        inputs: { label: 'Order type', placeholder: 'Choose a type' },
        children: [
          { component: 'option', inputs: { value: 'standard' }, children: ['Standard'] },
          { component: 'option', inputs: { value: 'renewal' }, children: ['Renewal'] },
          { component: 'option', inputs: { value: 'replacement' }, children: ['Replacement'] },
        ],
      },
    },
    {
      key: 'chosen-and-refused',
      label: 'Chosen, and refused',
      summary:
        'A field with a value already selected beside one the page has refused — the same control, and the only ' +
        'thing separating them is what the page decided.',
      placement: [
        {
          component: 'caos-picklist-field',
          inputs: {
            label: 'Order type',
            value: 'renewal',
            options: [
              { value: 'standard', label: 'Standard' },
              { value: 'renewal', label: 'Renewal' },
            ],
          },
        },
        {
          component: 'caos-picklist-field',
          inputs: {
            label: 'Region',
            placeholder: 'Choose a region',
            required: true,
            invalid: true,
            message: 'A region is needed before this order can be submitted.',
            options: [
              { value: 'north', label: 'North' },
              { value: 'south', label: 'South' },
            ],
          },
        },
      ],
    },
    {
      key: 'dependent',
      label: 'One dropdown waiting on another',
      summary:
        'The Event field renders with no choices and its placeholder still showing until an Event kind is ' +
        'picked, then populates through an injected optionsSource — the cascading behaviour is wired by the ' +
        'page, never coded into the piece.',
      placement: [
        {
          component: 'caos-picklist-field',
          inputs: {
            label: 'Event kind',
            placeholder: 'Choose a kind',
            options: [
              { value: 'created', label: 'Created' },
              { value: 'updated', label: 'Updated' },
            ],
          },
        },
        {
          component: 'caos-picklist-field',
          inputs: {
            label: 'Event',
            placeholder: 'Choose an event kind first',
            dependsOn: 'event_kind',
          },
        },
      ],
      host: {
        summary:
          'wires the Event field to the Event kind field beside it: an optionsSource answering from the kind, ' +
          "and dependsOnValue kept in sync with the kind field's own value on every change.",
        run: (stage) => {
          const [kindField, eventField] = stage.querySelectorAll('caos-picklist-field');
          if (!kindField || !eventField) return;
          eventField.optionsSource = (kind) => EVENTS_BY_KIND[kind] ?? [];
          kindField.addEventListener('change', () => {
            eventField.dependsOnValue = kindField.value;
          });
        },
      },
    },
    {
      key: 'qualifier-filterable',
      label: 'A long list, told apart by what each one is',
      summary:
        'Filterable draws a qualifier under each option and searches both lines when typing — the difference ' +
        'between a usable picker and an alphabetical wall once a set runs to hundreds of entries.',
      stage: { opensBelow: 200 },
      placement: {
        component: 'caos-picklist-field',
        inputs: {
          label: 'Object',
          placeholder: 'Search objects…',
          filterable: true,
          options: [
            { value: 'account', label: 'Account', qualifier: 'Standard object' },
            { value: 'contact', label: 'Contact', qualifier: 'Standard object' },
            { value: 'acme__project', label: 'Project', qualifier: 'Custom object' },
            { value: 'acme__shipment', label: 'Shipment', qualifier: 'Custom object' },
          ],
        },
      },
    },
    {
      key: 'several-values',
      label: 'Several values',
      summary: 'Multiple set: the ways a contact may be reached, as one box per option, with email already ticked.',
      placement: {
        component: 'caos-picklist-field',
        inputs: {
          label: 'Contact by',
          multiple: true,
          options: [
            { value: 'email', label: 'Email' },
            { value: 'phone', label: 'Phone' },
            { value: 'sms', label: 'Text message' },
            { value: 'post', label: 'Post' },
          ],
          values: ['email'],
        },
      },
    },
    {
      key: 'several-required',
      label: 'Several values, one required',
      summary: 'A required group: clear the one ticked box and it turns red and says a choice is needed.',
      placement: {
        component: 'caos-picklist-field',
        inputs: {
          label: 'Regions served',
          multiple: true,
          required: true,
          options: [
            { value: 'north', label: 'North' },
            { value: 'south', label: 'South' },
            { value: 'east', label: 'East' },
          ],
          values: ['north'],
        },
      },
    },
    {
      key: 'several-disabled',
      label: 'Several values, not changeable',
      summary: 'The same group disabled: what is chosen still reads clearly, and none of the boxes can be changed.',
      placement: {
        component: 'caos-picklist-field',
        inputs: {
          label: 'Contact by',
          multiple: true,
          disabled: true,
          options: [
            { value: 'email', label: 'Email' },
            { value: 'phone', label: 'Phone' },
            { value: 'sms', label: 'Text message' },
          ],
          values: ['email', 'sms'],
        },
      },
    },
  ],
};

export default docs;
